import { useCallback, useEffect, useMemo, useState } from 'react';
import { UmsatzWert } from '@/components/UmsatzSperre';
import { reportingAPI } from '@/lib/api';
import { toast } from 'sonner';
import { BarChart3, Download, Plus, Trash2, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import SeitenaufrufeKarte from '@/components/SeitenaufrufeKarte';
import { Button } from '@/components/ui/button';

/* Reporting & Attribution (Erweiterung v2, B4) — nur Inhaber/Leitung.
   Eine Tabelle Kampagnen × Kennzahlen aus dem Ereignisstrom, Zeitraumfilter,
   Mandantenumschalter, CSV-Export, zwei feste Monatsverläufe. Bewusst kein
   Diagramm-Baukasten. */

const MANDANTEN = ['p1', 'ptl', 'zinzino'];

const monatsanfang = () => new Date().toISOString().slice(0, 8) + '01';
const heute = () => new Date().toISOString().slice(0, 10);

const fmt = (v, einheit = '') => (v === null || v === undefined ? '—' : `${v}${einheit}`);
const eur = (v) => (v === null || v === undefined ? '—'
  : v.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' }));

function Verlauf({ daten, feld, titel }) {
  const max = Math.max(1, ...daten.map((d) => d[feld]));
  return (
    <div>
      <p className="text-xs font-medium mb-2">{titel}</p>
      <div className="flex items-end gap-1 h-24">
        {daten.map((d) => (
          <div key={d.monat} className="flex-1 flex flex-col items-center gap-1 min-w-0"
               title={`${d.monat}: ${d[feld]}`}>
            <span className="text-[10px] text-muted-foreground">{d[feld] || ''}</span>
            <div className="w-full rounded-t bg-primary/70"
                 style={{ height: `${(d[feld] / max) * 100 || 2}%`, minHeight: 2 }} />
            <span className="text-[9px] text-muted-foreground rotate-0">{d.monat.slice(5)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ReportingPage({ eingebettet = false }) {
  const [mandant, setMandant] = useState('p1');
  const [von, setVon] = useState(monatsanfang());
  const [bis, setBis] = useState(heute());
  const [daten, setDaten] = useState(null);
  const [verlauf, setVerlauf] = useState([]);
  const [kosten, setKosten] = useState([]);
  const [trichter, setTrichter] = useState(null);
  const [lade, setLade] = useState(false);
  const [neu, setNeu] = useState({ kanal: 'meta', kampagne: '', von: monatsanfang(), bis: heute(), betrag: '' });

  const laden = useCallback(async () => {
    setLade(true);
    try {
      const [k, v, w, t] = await Promise.all([
        reportingAPI.kennzahlen({ von, bis, mandant }),
        reportingAPI.verlauf(mandant),
        reportingAPI.werbekosten(mandant),
        reportingAPI.trichterKanal({ von, bis }).catch(() => ({ data: null })),
      ]);
      setDaten(k.data); setVerlauf(v.data); setKosten(w.data); setTrichter(t.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reporting konnte nicht geladen werden');
    }
    setLade(false);
  }, [von, bis, mandant]);
  useEffect(() => { laden(); }, [laden]);

  const exportCsv = async () => {
    try {
      const r = await reportingAPI.exportCSV({ von, bis, mandant });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = Object.assign(document.createElement('a'),
        { href: url, download: `reporting_${mandant}_${von}_${bis}.csv` });
      a.click(); URL.revokeObjectURL(url);
    } catch { toast.error('Export fehlgeschlagen'); }
  };

  const kostenAnlegen = async () => {
    try {
      await reportingAPI.werbekostenAnlegen({ ...neu, betrag: Number(neu.betrag || 0), mandant });
      toast.success('Werbekosten erfasst');
      setNeu((n) => ({ ...n, kampagne: '', betrag: '' }));
      laden();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Speichern fehlgeschlagen'); }
  };

  const zeilen = useMemo(() => (daten ? [...daten.kampagnen, daten.gesamt] : []), [daten]);

  return (
    <div className={eingebettet ? 'space-y-5' : 'p-4 md:p-6 max-w-6xl mx-auto space-y-5'} data-testid="reporting">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          {!eingebettet && (
            <h1 className="text-xl md:text-2xl font-bold flex items-center gap-2">
              <BarChart3 className="h-6 w-6 text-primary" /> Reporting
            </h1>
          )}
          <p className="text-sm text-muted-foreground mt-1">
            Kampagnen × Kennzahlen aus dem Ereignisstrom — Kosten je Kampagne
            trägst du wöchentlich aus dem Werbeanzeigenmanager ein.
          </p>
        </div>
        <Button size="sm" variant="outline" className="gap-1.5" onClick={exportCsv}>
          <Download className="h-4 w-4" /> CSV
        </Button>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-2 flex-wrap">
        <select value={mandant} onChange={(e) => setMandant(e.target.value)}
                className="text-sm rounded-md border border-border bg-background px-2 py-1.5">
          {MANDANTEN.map((m) => <option key={m} value={m}>{m.toUpperCase()}</option>)}
        </select>
        <input type="date" value={von} onChange={(e) => setVon(e.target.value)}
               className="text-sm rounded-md border border-border bg-background px-2 py-1" />
        <span className="text-muted-foreground text-sm">bis</span>
        <input type="date" value={bis} onChange={(e) => setBis(e.target.value)}
               className="text-sm rounded-md border border-border bg-background px-2 py-1" />
        {lade && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
      </div>

      {/* Kennzahlen-Tabelle */}
      <Card>
        <CardContent className="pt-4 overflow-x-auto">
          <table className="w-full text-sm whitespace-nowrap">
            <thead>
              <tr className="text-left text-xs text-muted-foreground border-b border-border">
                <th className="py-2 pr-3">Kampagne</th>
                <th className="py-2 px-2 text-right">Leads</th>
                <th className="py-2 px-2 text-right">Termine</th>
                <th className="py-2 px-2 text-right">Show-Rate</th>
                <th className="py-2 px-2 text-right">Abschlüsse</th>
                <th className="py-2 px-2 text-right">Lead→Termin</th>
                <th className="py-2 px-2 text-right">Termin→Abschluss</th>
                <th className="py-2 px-2 text-right">Umsatz</th>
                <th className="py-2 px-2 text-right">Kosten</th>
                <th className="py-2 px-2 text-right">CPL</th>
                <th className="py-2 px-2 text-right">CPA</th>
                <th className="py-2 pl-2 text-right">Ø Tage bis Abschluss</th>
              </tr>
            </thead>
            <tbody>
              {zeilen.map((z) => (
                <tr key={z.kampagne}
                    className={`border-b border-border/50 ${z.kampagne === 'GESAMT' ? 'font-semibold bg-muted/40' : ''}`}>
                  <td className="py-1.5 pr-3">{z.kampagne}</td>
                  <td className="py-1.5 px-2 text-right font-mono">{z.leads}</td>
                  <td className="py-1.5 px-2 text-right font-mono">{z.termine}</td>
                  <td className="py-1.5 px-2 text-right font-mono">{fmt(z.show_rate, ' %')}</td>
                  <td className="py-1.5 px-2 text-right font-mono">{z.abschluesse}</td>
                  <td className="py-1.5 px-2 text-right font-mono">{fmt(z.lead_termin_quote, ' %')}</td>
                  <td className="py-1.5 px-2 text-right font-mono">{fmt(z.termin_abschluss_quote, ' %')}</td>
                  <td className="py-1.5 px-2 text-right font-mono"><UmsatzWert wert={z.umsatz} format={eur} kurz /></td>
                  <td className="py-1.5 px-2 text-right font-mono">{eur(z.werbekosten)}</td>
                  <td className="py-1.5 px-2 text-right font-mono">{eur(z.cpl)}</td>
                  <td className="py-1.5 px-2 text-right font-mono">{eur(z.cpa)}</td>
                  <td className="py-1.5 pl-2 text-right font-mono">{fmt(z.durchlaufzeit_median_tage)}</td>
                </tr>
              ))}
              {!zeilen.length && !lade && (
                <tr><td colSpan={12} className="py-6 text-center text-muted-foreground">
                  Noch keine Ereignisse im Zeitraum — der Strom füllt sich mit jeder
                  Funnel-Buchung automatisch.
                </td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Konversionstrichter je Herkunftskanal (22.09.2026) */}
      <Card data-testid="trichter-kanal">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Trichter je Herkunftskanal</CardTitle>
          <p className="text-xs text-muted-foreground">Leads nach Anlagedatum im Zeitraum, aus den Lead-Akten (alle Mandanten) — Lead → Probetraining besucht → Abschluss</p>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm whitespace-nowrap">
            <thead>
              <tr className="text-left text-xs text-muted-foreground border-b border-border">
                <th className="py-2 pr-3">Kanal</th>
                <th className="py-2 px-2 text-right">Leads</th>
                <th className="py-2 px-2 text-right">Probetrainings</th>
                <th className="py-2 px-2 text-right">Abschlüsse</th>
                <th className="py-2 px-2 text-right">Verloren</th>
                <th className="py-2 px-2 text-right">Lead→Probe</th>
                <th className="py-2 px-2 text-right">Probe→Abschluss</th>
                <th className="py-2 pl-2 text-right">Lead→Abschluss</th>
              </tr>
            </thead>
            <tbody>
              {(trichter ? [...trichter.kanaele, trichter.gesamt] : []).map((z) => (
                <tr key={z.kanal || 'ohne'} className={`border-b border-border/50 ${z.kanal === 'GESAMT' ? 'font-semibold bg-muted/40' : ''}`}>
                  <td className="py-1.5 pr-3">{z.label}</td>
                  <td className="py-1.5 px-2 text-right font-mono">{z.leads}</td>
                  <td className="py-1.5 px-2 text-right font-mono">{z.probetrainings}</td>
                  <td className="py-1.5 px-2 text-right font-mono">{z.abschluesse}</td>
                  <td className="py-1.5 px-2 text-right font-mono">{z.verloren}</td>
                  <td className="py-1.5 px-2 text-right font-mono">{fmt(z.lead_probe_quote, ' %')}</td>
                  <td className="py-1.5 px-2 text-right font-mono">{fmt(z.probe_abschluss_quote, ' %')}</td>
                  <td className="py-1.5 pl-2 text-right font-mono">{fmt(z.lead_abschluss_quote, ' %')}</td>
                </tr>
              ))}
              {trichter && !trichter.kanaele.length && (
                <tr><td colSpan={8} className="py-6 text-center text-muted-foreground">Keine Leads im Zeitraum.</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Seitenaufrufe der Landingpages */}
      <SeitenaufrufeKarte />

      {/* Verläufe */}
      {verlauf.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Monatsverläufe</CardTitle></CardHeader>
          <CardContent className="grid md:grid-cols-2 gap-6">
            <Verlauf daten={verlauf} feld="leads" titel="Leads je Monat" />
            <Verlauf daten={verlauf} feld="abschluesse" titel="Abschlüsse je Monat" />
          </CardContent>
        </Card>
      )}

      {/* Werbekosten */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Werbekosten ({mandant.toUpperCase()})</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-end gap-2 flex-wrap">
            {[['kanal', 'Kanal', 'text'], ['kampagne', 'Kampagne', 'text'],
              ['von', 'Von', 'date'], ['bis', 'Bis', 'date'], ['betrag', 'Betrag €', 'number']]
              .map(([f, label, typ]) => (
                <div key={f}>
                  <label className="text-xs text-muted-foreground block mb-1">{label}</label>
                  <input type={typ} value={neu[f]}
                         onChange={(e) => setNeu((n) => ({ ...n, [f]: e.target.value }))}
                         className="text-sm rounded-md border border-border bg-background px-2 py-1 w-32" />
                </div>
              ))}
            <Button size="sm" className="gap-1" onClick={kostenAnlegen}
                    disabled={!neu.kampagne || !neu.betrag}>
              <Plus className="h-4 w-4" /> Erfassen
            </Button>
          </div>
          {kosten.length > 0 && (
            <table className="text-sm w-full max-w-xl">
              <tbody>
                {kosten.slice(0, 10).map((k) => (
                  <tr key={k.id} className="border-b border-border/40">
                    <td className="py-1 pr-2">{k.kanal}</td>
                    <td className="py-1 pr-2">{k.kampagne}</td>
                    <td className="py-1 pr-2 text-muted-foreground">{k.von} – {k.bis}</td>
                    <td className="py-1 pr-2 text-right font-mono">{eur(k.betrag)}</td>
                    <td className="py-1 text-right">
                      <button className="text-muted-foreground hover:text-destructive"
                              onClick={async () => { await reportingAPI.werbekostenLoeschen(k.id); laden(); }}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
